import React, { useState, useEffect } from "react";
import {
  Box, Typography, Paper, Button, CircularProgress,
  Chip, TextField, Divider, Alert, Grid
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { db } from "../firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { importaStorico } from "../utils/importStorico";

const SEZIONI_CALENDAR = [
  {
    id: "invitatiEvento",
    titolo: "Reminder evento",
    descrizione: "Calendario proprietario: segreteria@algores.it",
    placeholder: "email@esempio.com",
  },
  {
    id: "invitatiDEM",
    titolo: "Reminder DEM e Survey",
    descrizione: "Calendario proprietario: c_b4b24nd83eolg6eqk05rmb9cq4@group.calendar.google.com",
    placeholder: "email@esempio.com",
  },
  {
    id: "invitatiSuiteCRM",
    titolo: "Reminder task SuiteCRM",
    descrizione: "Calendario proprietario: c_b4b24nd83eolg6eqk05rmb9cq4@group.calendar.google.com",
    placeholder: "email@esempio.com",
  },
];

export default function Impostazioni() {
  const [impostazioni, setImpostazioni] = useState({
    invitatiEvento: [],
    invitatiDEM: [],
    invitatiSuiteCRM: [],
  });
  const [tariffe, setTariffe] = useState({
    tariffeOrariaAlgores: 40,
    tariffeOrariaCollaboratori: 15,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [savingTariffe, setSavingTariffe] = useState(false);
  const [savedTariffe, setSavedTariffe] = useState(false);
  const [nuoviValori, setNuoviValori] = useState({
    invitatiEvento: "",
    invitatiDEM: "",
    invitatiSuiteCRM: "",
  });
  const [importando, setImportando] = useState(false);
  const [progressoImport, setProgressoImport] = useState(null);
  const [risultatoImport, setRisultatoImport] = useState(null);

  useEffect(() => {
    Promise.all([
      getDoc(doc(db, "impostazioni", "calendario")),
      getDoc(doc(db, "impostazioni", "tariffe")),
    ]).then(([snapCal, snapTariffe]) => {
      if (snapCal.exists()) {
        setImpostazioni((prev) => ({ ...prev, ...snapCal.data() }));
      }
      if (snapTariffe.exists()) {
        setTariffe((prev) => ({ ...prev, ...snapTariffe.data() }));
      }
      setLoading(false);
    });
  }, []);

  function aggiungiInvitato(sezioneId) {
    const valore = nuoviValori[sezioneId].trim();
    if (!valore) return;
    if (impostazioni[sezioneId].includes(valore)) return;
    setImpostazioni((prev) => ({
      ...prev,
      [sezioneId]: [...prev[sezioneId], valore],
    }));
    setNuoviValori((prev) => ({ ...prev, [sezioneId]: "" }));
  }

  function rimuoviInvitato(sezioneId, valore) {
    setImpostazioni((prev) => ({
      ...prev,
      [sezioneId]: prev[sezioneId].filter((v) => v !== valore),
    }));
  }

  async function handleSalva() {
    setSaving(true);
    try {
      await setDoc(doc(db, "impostazioni", "calendario"), impostazioni);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error("Errore salvataggio impostazioni:", error);
    }
    setSaving(false);
  }

  async function handleSalvaTariffe() {
    setSavingTariffe(true);
    try {
      await setDoc(doc(db, "impostazioni", "tariffe"), tariffe);
      setSavedTariffe(true);
      setTimeout(() => setSavedTariffe(false), 3000);
    } catch (error) {
      console.error("Errore salvataggio tariffe:", error);
    }
    setSavingTariffe(false);
  }

  async function handleImportStorico(e) {
    const file = e.target.files[0];
    if (!file) return;
    setImportando(true);
    setRisultatoImport(null);
    try {
      const risultato = await importaStorico(file, (current, total, importati, aggiornati, saltati) => {
        setProgressoImport({ current, total, importati, aggiornati, saltati });
      });
      setRisultatoImport(risultato);
    } catch (error) {
      console.error("Errore import:", error);
      setRisultatoImport({ errore: error.message });
    }
    setImportando(false);
    setProgressoImport(null);
  }

  if (loading) return <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>;

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" mb={3}>
        Impostazioni
      </Typography>

      {/* CALENDARI */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" mb={1} color="primary">
          Google Calendar — Invitati ai reminder
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Per ciascun tipo di reminder configura gli indirizzi email da invitare.
        </Typography>

        {SEZIONI_CALENDAR.map((sezione, idx) => (
          <Box key={sezione.id}>
            {idx > 0 && <Divider sx={{ my: 3 }} />}
            <Typography fontWeight="bold" mb={0.5}>{sezione.titolo}</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              {sezione.descrizione}
            </Typography>

            <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
              {impostazioni[sezione.id].length === 0 && (
                <Typography variant="body2" color="text.disabled">
                  Nessun invitato configurato
                </Typography>
              )}
              {impostazioni[sezione.id].map((email) => (
                <Chip
                  key={email}
                  label={email}
                  onDelete={() => rimuoviInvitato(sezione.id, email)}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>

            <Box display="flex" gap={1} alignItems="center">
              <TextField
                size="small"
                placeholder={sezione.placeholder}
                value={nuoviValori[sezione.id]}
                onChange={(e) => setNuoviValori((prev) => ({
                  ...prev, [sezione.id]: e.target.value
                }))}
                onKeyDown={(e) => e.key === "Enter" && aggiungiInvitato(sezione.id)}
                sx={{ flex: 1 }}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => aggiungiInvitato(sezione.id)}
              >
                Aggiungi
              </Button>
            </Box>
          </Box>
        ))}

        <Box mt={4} display="flex" alignItems="center" gap={2}>
          <Button variant="contained" onClick={handleSalva} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : "Salva impostazioni"}
          </Button>
          {saved && <Alert severity="success" sx={{ py: 0 }}>Salvato con successo</Alert>}
        </Box>
      </Paper>

      {/* TARIFFE ORARIE */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" mb={1} color="primary">
          Tariffe orarie
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Tariffe utilizzate per il calcolo del costo del lavoro nell'analisi costi/ricavi degli eventi.
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Tariffa oraria Algores (€/h)"
              type="number"
              value={tariffe.tariffeOrariaAlgores}
              onChange={(e) => setTariffe((prev) => ({
                ...prev, tariffeOrariaAlgores: parseFloat(e.target.value) || 0
              }))}
              inputProps={{ min: 0, step: 0.5 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Tariffa oraria Collaboratori (€/h)"
              type="number"
              value={tariffe.tariffeOrariaCollaboratori}
              onChange={(e) => setTariffe((prev) => ({
                ...prev, tariffeOrariaCollaboratori: parseFloat(e.target.value) || 0
              }))}
              inputProps={{ min: 0, step: 0.5 }}
            />
          </Grid>
        </Grid>
        <Box mt={3} display="flex" alignItems="center" gap={2}>
          <Button variant="contained" onClick={handleSalvaTariffe} disabled={savingTariffe}>
            {savingTariffe ? <CircularProgress size={20} /> : "Salva tariffe"}
          </Button>
          {savedTariffe && <Alert severity="success" sx={{ py: 0 }}>Salvato con successo</Alert>}
        </Box>
      </Paper>

      {/* IMPORT STORICO */}
      <Paper elevation={1} sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight="bold" mb={1} color="primary">
          Import storico eventi
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Importa i dati storici dal file Excel <strong>Tutti_Eventi_VMA.xlsx</strong>.
          Gli eventi già presenti vengono aggiornati se ci sono differenze.
        </Typography>

        <Button
          variant="outlined"
          component="label"
          disabled={importando}
          startIcon={importando ? <CircularProgress size={18} /> : null}
        >
          {importando ? "Importazione in corso..." : "Seleziona file Excel"}
          <input type="file" accept=".xlsx" hidden onChange={handleImportStorico} />
        </Button>

        {progressoImport && (
          <Box mt={2}>
            <Typography variant="body2">
              Elaborazione {progressoImport.current}/{progressoImport.total} —
              Importati: {progressoImport.importati} —
              Aggiornati: {progressoImport.aggiornati} —
              Saltati: {progressoImport.saltati}
            </Typography>
          </Box>
        )}

        {risultatoImport && !risultatoImport.errore && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Import completato — {risultatoImport.importati} nuovi eventi importati,{" "}
            {risultatoImport.aggiornati} eventi aggiornati,{" "}
            {risultatoImport.saltati} invariati
          </Alert>
        )}

        {risultatoImport?.errore && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Errore: {risultatoImport.errore}
          </Alert>
        )}
      </Paper>
    </Box>
  );
}